export type ThemeMode = 'dark' | 'light';

export interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  background: {
    main: string;
    card: string;
    alt: string;
  };
  text: {
    primary: string;
    secondary: string;
    accent: string;
  };
  success: string;
  warning: string;
  error: string;
  info: string;
  border: string;
  /** Tinted section surfaces. Bands of colour rather than two off-whites. */
  surface: {
    blue: string;
    gold: string;
    /** A deep band for high-contrast sections — inverted type sits on this. */
    ink: string;
  };
  /** Boundary for interactive controls. WCAG 1.4.11 wants 3:1 against the
   *  adjacent surface, which the decorative `border` hairline does not meet. */
  inputBorder: string;
  gradient: {
    primary: string;
    secondary: string;
  };
  foreground: string;
  'muted-foreground': string;
  input: string;
  destructive: string;
  'destructive-foreground': string;
}

export interface Theme {
  mode: ThemeMode;
  colors: ThemeColors;
  borderRadius: {
    sm: string;
    md: string;
    lg: string;
    xl: string;
    full: string;
  };
  boxShadow: {
    sm: string;
    md: string;
    lg: string;
  };
  spacing: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    xxl: string;
  };
  typography: {
    fontFamily: {
      base: string;
      accent: string;
    };
    fontSize: {
      xs: string;
      sm: string;
      md: string;
      lg: string;
      xl: string;
      xxl: string;
      display: string;
    };
    fontWeight: {
      light: number;
      regular: number;
      medium: number;
      bold: number;
      black: number;
    };
  };
}

/**
 * Palette is derived from the Mindalitix Academy logo, not from the previous
 * indigo theme: the wordmark is gold #FCB400 and the head mark is a blue that
 * runs #003CC0 → #0C60E4. Blue therefore carries every action and link, gold is
 * the sparing accent, and the neutrals are warmed slightly so both sit
 * comfortably on the page.
 */
export const brand = {
  blue: '#0B3FC4',
  blueDeep: '#062C93',
  blueLight: '#7FA5FF',
  gold: '#FCB400',
  goldDeep: '#8A5D00', // gold dark enough to be readable as text on off-white
  ink: '#17181C',
} as const;

export const darkTheme: Theme = {
  mode: 'dark',
  colors: {
    primary: '#7FA5FF', // Brand blue lifted for legibility on ink
    secondary: brand.gold,
    accent: brand.gold,
    background: {
      main: '#131211', // Warm ink rather than pure black — sits with the gold
      card: '#1B1A18',
      alt: '#232220',
    },
    text: {
      primary: '#EDEBE8',
      secondary: '#A6A39D',
      accent: '#7FA5FF',
    },
    success: '#34D399',
    warning: '#FBBF24',
    error: '#F87171',
    info: '#7DD3FC',
    border: '#302E2B',
    surface: { blue: '#151A26', gold: '#221D12', ink: '#0C0B0A' },
    inputBorder: '#7A756B', // 3.5:1 on the dark input surface
    gradient: {
      primary: 'from-primary to-primary',
      secondary: 'from-background-alt to-background-card',
    },
    foreground: '#EDEBE8',
    'muted-foreground': '#A6A39D',
    input: '#232220',
    destructive: '#F87171',
    'destructive-foreground': '#131211',
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.5rem',
    lg: '1rem',
    xl: '1.5rem',
    full: '9999px',
  },
  boxShadow: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
  },
  spacing: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem',
    xxl: '5rem',
  },
  typography: {
    fontFamily: {
      base: 'Inter, system-ui, sans-serif',
      accent: 'Fraunces, Georgia, serif',
    },
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      md: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      xxl: '1.5rem',
      display: '3rem',
    },
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      bold: 700,
      black: 900,
    },
  },
};

export const lightTheme: Theme = {
  mode: 'light',
  colors: {
    primary: brand.blue, // Logo blue — the single confident accent
    secondary: brand.goldDeep, // Readable gold, for text and icons that need the accent
    accent: brand.gold, // Full-strength logo gold — fills and marks only, never text
    background: {
      main: '#FBFAF8', // Warm off-white; cool white fights the gold
      card: '#FFFFFF',
      alt: '#F4F2EE',
    },
    text: {
      primary: brand.ink, // Ink, not black — easier to read at length
      secondary: '#565A63',
      accent: brand.blue,
    },
    success: '#0E7C4A',
    warning: '#A85C00',
    error: '#C0271F',
    info: '#0B5FC4',
    border: '#E6E2DA',
    // Light enough to keep body copy at full contrast, saturated enough that a
    // band reads as deliberate colour rather than a smudge.
    surface: { blue: '#EEF3FD', gold: '#FDF6E7', ink: '#141A2E' },
    inputBorder: '#8C887F', // 3.5:1 on white — a field's edge must be visible
    gradient: {
      primary: 'from-primary to-primary',
      secondary: 'from-background-alt to-background-main',
    },
    foreground: brand.ink,
    'muted-foreground': '#565A63',
    input: '#FFFFFF',
    destructive: '#C0271F',
    'destructive-foreground': '#FFFFFF',
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.5rem',
    lg: '1rem',
    xl: '1.5rem',
    full: '9999px',
  },
  boxShadow: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
  },
  spacing: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem',
    xxl: '5rem',
  },
  typography: {
    fontFamily: {
      base: 'Inter, system-ui, sans-serif',
      accent: 'Fraunces, Georgia, serif',
    },
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      md: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      xxl: '1.5rem',
      display: '3rem',
    },
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      bold: 700,
      black: 900,
    },
  },
};

// Helper function to apply theme colors as CSS variables
export const applyThemeColorsToCssVariables = (colors: ThemeColors) => {
  const root = document.documentElement;
  root.style.setProperty('--color-primary', colors.primary);
  root.style.setProperty('--color-secondary', colors.secondary);
  root.style.setProperty('--color-accent', colors.accent);

  root.style.setProperty('--color-background-main', colors.background.main);
  root.style.setProperty('--color-background-card', colors.background.card);
  root.style.setProperty('--color-background-alt', colors.background.alt);

  root.style.setProperty('--color-text-primary', colors.text.primary);
  root.style.setProperty('--color-text-secondary', colors.text.secondary);
  root.style.setProperty('--color-text-accent', colors.text.accent);

  root.style.setProperty('--color-success', colors.success);
  root.style.setProperty('--color-warning', colors.warning);
  root.style.setProperty('--color-error', colors.error);
  root.style.setProperty('--color-info', colors.info);
  root.style.setProperty('--color-border', colors.border);
  root.style.setProperty('--color-input-border', colors.inputBorder);
  root.style.setProperty('--color-surface-blue', colors.surface.blue);
  root.style.setProperty('--color-surface-gold', colors.surface.gold);
  root.style.setProperty('--color-surface-ink', colors.surface.ink);
  root.style.setProperty('--color-foreground', colors.foreground);
  root.style.setProperty('--color-muted-foreground', colors['muted-foreground']);
  root.style.setProperty('--color-input', colors.input);
  root.style.setProperty('--color-destructive', colors.destructive);
  root.style.setProperty('--color-destructive-foreground', colors['destructive-foreground']);

  // The gold is fixed across both themes — it is the logo colour, not a mode-dependent
  // surface — so it is exposed once here rather than duplicated in each theme object.
  root.style.setProperty('--color-brand-gold', '#FCB400');

  // Keeps the browser UI (mobile address bar, PWA splash) in step with the theme.
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', colors.background.main);
};


